import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { CheckCircle2, Lock } from 'lucide-react';
export function UnlockReport() {
  const navigate = useNavigate();
  const {
    reportId
  } = useParams();
  const location = useLocation();
  // Detect product type from URL path
  const getProductType = () => {
    if (location.pathname.includes('/work/')) return 'work';
    if (location.pathname.includes('/child/')) return 'child';
    if (location.pathname.includes('/relationship/')) return 'relationship';
    return 'self';
  };
  const product = getProductType();
  const getProductConfig = () => {
    switch (product) {
      case 'work':
        return {
          title: 'Work System Diagnostic',
          icon: '💼',
          preview: ['Work System Architecture', 'Authority & Responsibility Pattern'],
          full: ['Decision-Making Under Pressure', 'Execution vs Leadership Dynamics', 'Work Environment Fit', 'Stress & Burnout Pattern', 'Collaboration Style', 'Career Trajectory Pattern', 'Work System Summary']
        };
      case 'child':
        return {
          title: 'Child System Map',
          icon: '👶',
          preview: ['Core Temperament & Nervous System Type', 'Sensitivity Zones'],
          full: ['Regulation Patterns', 'Learning & Energy Style', 'Environment Fit', 'Developmental Pressure Points', 'Parent Translation Layer', 'Pattern Consistency Check', 'Integration Summary']
        };
      case 'relationship':
        return {
          title: 'Relationship Dynamics Diagnostic',
          icon: '💞',
          preview: ['System A & System B Architecture', 'Interaction Axis'],
          full: ['Friction & Harmony Mechanics', 'Power & Safety Dynamics', 'Conflict Loops', 'Long-Range Structural Risks', 'Relationship System Summary']
        };
      default:
        // self
        return {
          title: 'Self System Diagnostic',
          icon: '🧬',
          preview: ['Core System Architecture', 'Primary Behavioral Patterns'],
          full: ['Complete internal architecture analysis', 'System costs and limits', 'Life-stage pressure dynamics', 'Decision and commitment structures', 'Orientation mechanics (North Star)', 'Pattern tensions and mirrors', 'Full system summary']
        };
    }
  };
  const config = getProductConfig();
  const handleProceedToPayment = () => {
    const paymentPath = product === 'self' ? `/report/${reportId}/pay` : `/report/${product}/${reportId}/pay`;
    navigate(paymentPath);
  };
  return <>
      <Helmet data-id="element-3962">
        <title data-id="element-3963">Unlock {config.title} | Lazy Monk Labs</title>
        <meta name="robots" content="noindex, nofollow" data-id="element-3964" />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-3965">
        {/* Navigation */}
        <header data-id="element-3966">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-3967">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-3968">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-3969">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-3970" />
                <span className="text-lg font-medium tracking-tight" data-id="element-3971">
                  Lazy Monk Labs
                </span>
              </Link>
              <div className="flex items-center gap-8" data-id="element-3972">
                <Link to="/decode" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" data-id="element-3973">
                  Decode
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" data-id="element-3974">
                  Contact
                </a>
              </div>
            </div>
          </nav>
        </header>

        <main className="pt-32 pb-24 px-6" data-id="element-3975">
          <div className="max-w-3xl mx-auto" data-id="element-3976">
            <div className="text-center mb-12" data-id="element-3977">
              <div className="text-5xl mb-6" data-id="element-3978">{config.icon}</div>
              <h1 className="text-3xl md:text-4xl font-medium mb-6 leading-tight tracking-tight" data-id="element-3979">
                Unlock Full {config.title}
              </h1>
              <p className="text-xl text-[#F5F5F5]/70 leading-relaxed max-w-2xl mx-auto" data-id="element-3980">
                You are not buying a different report. You are unlocking the
                rest of this same diagnostic.
              </p>
            </div>

            {/* Pricing */}
            <div className="mb-12 text-center" data-id="element-3981">
              <div className="inline-block border border-[#2B2B2B] p-8 bg-[#111214]" data-id="element-3982">
                <div className="text-5xl font-medium mb-2" data-id="element-3983">$499</div>
                <div className="text-sm text-[#F5F5F5]/40 line-through mb-2" data-id="element-3984">
                  Regular: $1,499
                </div>
                <div className="text-xs text-[#64748B]" data-id="element-3985">
                  Launch pricing — Save $1,000
                </div>
              </div>
            </div>

            {/* What You've Seen */}
            <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-3986">
              <h2 className="text-xl font-medium mb-6" data-id="element-3987">
                What you've already seen
              </h2>
              <div className="space-y-3" data-id="element-3988">
                {config.preview.map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-3989">
                    <CheckCircle2 className="w-5 h-5 text-[#64748B] flex-shrink-0 mt-0.5" data-id="element-3990" />
                    <span className="text-[#F5F5F5]/70" data-id="element-3991">{item}</span>
                  </div>)}
              </div>
            </div>

            {/* What You'll Unlock */}
            <div className="mb-12" data-id="element-3992">
              <h2 className="text-xl font-medium mb-6" data-id="element-3993">What you'll unlock</h2>
              <div className="space-y-3" data-id="element-3994">
                {config.full.map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-3995">
                    <div className="w-5 h-5 border border-[#64748B] rounded flex-shrink-0 mt-0.5" data-id="element-3996" />
                    <span className="text-[#F5F5F5]/70" data-id="element-3997">{item}</span>
                  </div>)}
              </div>
            </div>

            {/* CTA */}
            <div className="border border-[#2B2B2B] p-8 bg-[#111214] text-center" data-id="element-3998">
              <p className="text-[#F5F5F5]/70 mb-8 leading-relaxed" data-id="element-3999">
                This is the rest of the same diagnostic you already generated.
                Unlocking gives you complete access to your full system
                analysis.
              </p>
              <button onClick={handleProceedToPayment} className="inline-block px-12 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-4000">
                Proceed to Payment →
              </button>
              <p className="mt-6 text-xs text-[#F5F5F5]/40" data-id="element-4001">
                One-time payment · Lifetime access
              </p>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="py-8 px-6 border-t border-[#2B2B2B]" data-id="element-4002">
          <div className="max-w-7xl mx-auto text-center" data-id="element-4003">
            <p className="text-xs text-[#F5F5F5]/40" data-id="element-4004">
              © {new Date().getFullYear()} Lazy Monk Labs. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>;
}